import { useEffect, useState } from 'react';
import { Button, Card, Notice, Select } from '../../components/ui';
import { LanguageSelector } from '../../components/LanguageSelector';
import { Logo } from '../../components/Logo';
import { trpc, type IdentityTrpcClient } from '../../lib/trpc';
import { useI18n } from '../../lib/i18n';

type Pending = Awaited<ReturnType<IdentityTrpcClient['googleFederation']['pending']['query']>>;
const defaultNavigate = (path: string) => window.location.assign(path);

export function GoogleContinuePage({ client = trpc, onNavigate = defaultNavigate }: { client?: IdentityTrpcClient; onNavigate?: (path: string) => void }) {
  const { t } = useI18n();
  const [pending, setPending] = useState<Pending>();
  const [selectedIdentityId, setSelectedIdentityId] = useState('');
  const [realmId, setRealmId] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    client.googleFederation.pending.query().then((result) => {
      if (!active) return;
      if (result.status === 'interaction') {
        onNavigate(`/realms/${encodeURIComponent(result.realmSlug)}/interaction/${encodeURIComponent(result.uid)}`);
        return;
      }
      if (result.status === 'session' || result.status === 'denied') { onNavigate('/conta/identidades'); return; }
      setPending(result);
      if (result.status === 'registration_required') setRealmId(result.realms[0]?.id ?? '');
    }).catch(() => { if (active) setPending({ status: 'failed', failure: null }); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [client, onNavigate]);

  async function complete() {
    if (!pending || (pending.status === 'identity_choice' && !selectedIdentityId) || (pending.status === 'registration_required' && !realmId)) return;
    setSubmitting(true); setError('');
    try {
      const input = pending.status === 'identity_choice' ? { action: 'select_identity' as const, selectedIdentityId } : { action: 'register' as const, realmId };
      const result = await client.googleFederation.completeDirect.mutate(input);
      if (result.status === 'identity_choice' || result.status === 'registration_required') {
        setPending(result);
        if (result.status === 'registration_required') setRealmId(result.realms[0]?.id ?? '');
        setSelectedIdentityId('');
      } else onNavigate('/');
    } catch { setError(t('googleContinueFailed')); } finally { setSubmitting(false); }
  }

  return <main className="auth-page"><div className="auth-brand"><Logo/><LanguageSelector/></div><Card className="auth-card">
    {loading ? <div className="auth-heading"><span className="spinner"/><p>{t('googleContinueLoading')}</p></div> : pending?.status === 'identity_choice' ? <>
      <div className="auth-heading"><h1>{t('publicIdentityChoiceTitle')}</h1><p>{t('publicIdentityChoiceDescription')}</p></div>
      {error && <Notice tone="danger">{error}</Notice>}
      <fieldset className="identity-choices"><legend className="sr-only">{t('publicAvailableIdentities')}</legend>{pending.identities.map((identity) => <label className="identity-choice" key={identity.id}><input type="radio" name="google-identity" value={identity.id} checked={selectedIdentityId === identity.id} onChange={() => setSelectedIdentityId(identity.id)}/><span><strong>{identity.displayName ?? t('unnamed')}</strong><small>{identity.email}</small></span></label>)}</fieldset>
      <Button className="full-button" disabled={!selectedIdentityId || submitting} onClick={() => void complete()}>{t('continue')}</Button>
    </> : pending?.status === 'registration_required' ? <>
      <div className="auth-heading"><h1>{t('publicCreateIdentityTitle')}</h1><p>{t('publicCreateIdentityDescription')}</p></div>
      {error && <Notice tone="danger">{error}</Notice>}
      <Select value={realmId} onChange={(event) => setRealmId(event.target.value)} disabled={submitting || pending.realms.length === 0}>{pending.realms.length ? pending.realms.map((realm) => <option key={realm.id} value={realm.id}>{realm.name} ({realm.slug})</option>) : <option value="">{t('navNoRealms')}</option>}</Select>
      <Button className="full-button" disabled={!realmId || submitting} onClick={() => void complete()}>{t('createIdentity')}</Button>
    </> : <>
      <div className="auth-heading"><h1>{t('cannotContinue')}</h1><p>{t('googleContinueFailed')}</p></div>
      <Button className="full-button" onClick={() => onNavigate('/')}>{t('backToLogin')}</Button>
    </>}
  </Card></main>;
}
